"use client";

import { useEffect, useMemo, useRef, useState } from "react";

type ApiItem = {
  name: string;
  folder: string;
  port: number;
  pm2: string;

  connected: boolean;
  connectedNumber?: string | null;

  // ✅ novos campos
  whatsStatus?: string; // connected | qr | disconnected | starting | unknown | offline
  hasQr?: boolean;
  queueSize?: number;
  withinWindow?: boolean;
  paused?: boolean;
  pausedUntil?: string | null;
  pauseReason?: string | null;

  metrics?: { sentToday?: number; sentTotal?: number; day?: string };
};

function bytesToGiB(b?: number) {
  if (b === undefined || b === null) return "-";
  return (b / 1024 / 1024 / 1024).toFixed(1) + " GiB";
}

function secToHuman(sec?: number) {
  if (sec === undefined || sec === null) return "-";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

function timeAgo(ts?: number) {
  if (!ts) return "-";
  const diff = Date.now() - ts;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

// ✅ Saúde da VPS por RAM livre (%)
function vpsHealth(ram?: { total: number; free: number }) {
  if (!ram?.total || ram.total <= 0) {
    return { label: "Sem dados", color: "rgba(255,255,255,0.14)", tone: "neutral" as const };
  }

  const percentFree = (ram.free / ram.total) * 100;

  if (percentFree >= 30) {
    return { label: "VPS Saudável", color: "rgba(24, 201, 100, 0.20)", tone: "ok" as const };
  }
  if (percentFree >= 15) {
    return { label: "Atenção", color: "rgba(234, 179, 8, 0.20)", tone: "warn" as const };
  }
  return { label: "Crítica", color: "rgba(255, 59, 92, 0.20)", tone: "bad" as const };
}

function fmtTimeMaybe(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString();
}

export default function Page() {
  const API = typeof window !== "undefined" ? `https://backpainel.automatiz3.com.br` : "";

  const REFRESH_MS = 5000; // fixo 5s

  const [apis, setApis] = useState<ApiItem[]>([]);
  const [system, setSystem] = useState<any>(null);

  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>("");

  const [errApis, setErrApis] = useState<string>("");
  const [errSystem, setErrSystem] = useState<string>("");

  const [onlyOffline, setOnlyOffline] = useState(false);
  const [sortOnlineFirst, setSortOnlineFirst] = useState(true);

  // meta por api
  const apiLastSeenRef = useRef<Record<string, number>>({});
  const [tick, setTick] = useState(0);

  // modal métricas
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [metricsApi, setMetricsApi] = useState<ApiItem | null>(null);

  async function fetchJson(url: string) {
    const r = await fetch(url, { cache: "no-store" });
    if (!r.ok) throw new Error(`HTTP ${r.status} em ${url}`);
    return r.json();
  }

  async function loadAll() {
    if (!API) return;

    setErrApis("");
    setErrSystem("");

    try {
      const j = await fetchJson(`${API}/apis`);
      const list: ApiItem[] = j?.apis || [];
      setApis(list);

      for (const it of list) apiLastSeenRef.current[it.folder] = Date.now();

      setLastUpdate(new Date().toLocaleString());
    } catch (e: any) {
      setApis([]);
      setErrApis(String(e?.message || e));
    }

    try {
      const j2 = await fetchJson(`${API}/system`);
      setSystem(j2);
    } catch (e: any) {
      setSystem(null);
      setErrSystem(String(e?.message || e));
    }
  }

  async function actionRestartLogout(folder: string) {
    if (!confirm(`Reiniciar / Logout da ${folder}? (apaga auth e reinicia)`)) return;
    setBusy(folder);
    try {
      await fetch(`${API}/apis/${folder}/logout`, { method: "POST" });
      await new Promise((r) => setTimeout(r, 1200));
      await loadAll();
    } finally {
      setBusy(null);
    }
  }

  async function actionUnpause(folder: string) {
    if (!confirm(`Tirar pausa (Safeguard) da ${folder}?`)) return;
    setBusy(folder);
    try {
      await fetch(`${API}/apis/${folder}/unpause`, { method: "POST" });
      await new Promise((r) => setTimeout(r, 600));
      await loadAll();
    } finally {
      setBusy(null);
    }
  }

  function openMetrics(a: ApiItem) {
    setMetricsApi(a);
    setMetricsOpen(true);
  }

  useEffect(() => {
    if (!API) return;

    loadAll().catch(() => {});
    const t = setInterval(() => loadAll().catch(() => {}), REFRESH_MS);
    const t2 = setInterval(() => setTick((x) => x + 1), 1000); // para "Atualizado há X"
    return () => {
      clearInterval(t);
      clearInterval(t2);
    };
  }, [API]);

  const view = useMemo(() => {
    let list = [...apis];

    if (onlyOffline) list = list.filter((a) => !a.connected);

    const qq = q.trim().toLowerCase();
    if (qq) {
      list = list.filter((a) =>
        `${a.folder} ${a.name} ${a.connectedNumber ?? ""} ${a.port}`.toLowerCase().includes(qq)
      );
    }

    if (sortOnlineFirst) {
      list.sort((a, b) => {
        const av = a.connected ? 0 : 1;
        const bv = b.connected ? 0 : 1;
        if (av !== bv) return av - bv;
        return a.folder.localeCompare(b.folder);
      });
    } else {
      list.sort((a, b) => a.folder.localeCompare(b.folder));
    }

    return list;
  }, [apis, q, onlyOffline, sortOnlineFirst]);

  const total = apis.length;
  const connected = apis.filter((a) => a.connected).length;
  const disconnected = total - connected;

  // trigger rerender for timeAgo()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _ = tick;

  const health = vpsHealth(system?.ram);

  const ramUsed = system?.ram?.used;
  const ramTotal = system?.ram?.total;
  const ramLine =
    ramUsed != null && ramTotal != null ? `${bytesToGiB(ramUsed)} / ${bytesToGiB(ramTotal)}` : "-";

  return (
    <div style={styles.page}>
      <div style={styles.topbar}>
        <div>
          <div style={styles.h1}>Painel Automatiz3</div>
          <div style={styles.p}>
            Total: <b>{total}</b> • Conectadas: <b style={{ color: "#77f5b1" }}>{connected}</b> •
            Desconectadas: <b style={{ color: "#ffb3c0" }}>{disconnected}</b>
            {lastUpdate ? <span style={{ opacity: 0.7 }}> • Atualizado: {lastUpdate}</span> : null}
          </div>
        </div>

        <div style={styles.controls}>
          <input
            style={{ ...styles.input, width: 320 }}
            placeholder="Busca (opcional) — api, número, porta..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={sortOnlineFirst}
              onChange={(e) => setSortOnlineFirst(e.target.checked)}
            />
            <span>Online primeiro</span>
          </label>

          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={onlyOffline}
              onChange={(e) => setOnlyOffline(e.target.checked)}
            />
            <span>Somente offline</span>
          </label>

          <button style={styles.btnGhost} onClick={() => loadAll()}>
            Atualizar
          </button>
        </div>
      </div>

      {(errApis || errSystem) && (
        <div style={styles.errorCard}>
          <div style={{ fontWeight: 900, marginBottom: 8 }}>⚠️ Erro no painel</div>
          {errApis && (
            <div style={styles.small}>
              <b>/apis:</b> {errApis}
            </div>
          )}
          {errSystem && (
            <div style={styles.small}>
              <b>/system:</b> {errSystem}
            </div>
          )}
        </div>
      )}

      {/* ✅ VPS compacto */}
      {system?.ok && (
        <div style={styles.vpsCompact}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 16, fontWeight: 900 }}>Saúde da VPS</div>
                <span style={{ ...styles.healthBadge, background: health.color }}>{health.label}</span>
              </div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>
                Host: <b>{system.hostname}</b> • Uptime: <b>{secToHuman(system.uptimeSec)}</b> • Refresh:{" "}
                <b>{Math.floor(REFRESH_MS / 1000)}s</b>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <div style={styles.vpsPill}>
                <div style={styles.vpsPillLabel}>RAM</div>
                <div style={styles.vpsPillValue}>{ramLine}</div>
              </div>
              <div style={styles.vpsPill}>
                <div style={styles.vpsPillLabel}>CPU</div>
                <div style={styles.vpsPillValue}>
                  {system.cpu?.cores ?? "-"} cores • Load 1m{" "}
                  {typeof system.loadavg?.[0] === "number" ? system.loadavg[0].toFixed(2) : "-"}
                </div>
              </div>
              <div style={styles.vpsPill}>
                <div style={styles.vpsPillLabel}>RAM Livre</div>
                <div style={styles.vpsPillValue}>{bytesToGiB(system.ram?.free)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ GRID FIXO 3 COLUNAS */}
      <div style={styles.grid3}>
        {view.map((a) => {
          const lastSeen = apiLastSeenRef.current[a.folder];
          const lastSeenText = lastSeen ? `Atualizado há ${timeAgo(lastSeen)}` : "Sem atualização";

          const totalSent = a.metrics?.sentTotal ?? 0;

          // ✅ status label mais completo (connected / qr / disconnected)
          const st = a.whatsStatus || (a.connected ? "connected" : "disconnected");
          const statusLabel =
            st === "connected" ? "CONECTADA" : st === "qr" ? "QR" : "DESCONECTADA";

          const statusStyle =
            st === "connected"
              ? styles.badgeOk
              : st === "qr"
              ? styles.badgeQr
              : styles.badgeOff;

          const queue = a.queueSize ?? 0;
          const within = a.withinWindow !== false;
          const paused = !!a.paused;

          const pausedTime = fmtTimeMaybe(a.pausedUntil || null);
          const pausedLine = paused
            ? pausedTime
              ? `Pausado até ${pausedTime}`
              : "Pausado"
            : "Ativo";

          const windowLine = within ? "Janela: OK (08–19)" : "Janela: Fora do horário";

          return (
            <div key={a.folder} style={styles.apiCardCompact}>
              <div style={styles.apiHeader}>
                <div>
                  <div style={styles.apiName}>{a.folder}</div>
                  <div style={styles.apiSub}>
                    Porta: <b>{a.port}</b> • Número: <b>{a.connectedNumber || "-"}</b>
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                  <div style={statusStyle}>{statusLabel}</div>
                  <div style={styles.badgeMeta}>{lastSeenText}</div>
                </div>
              </div>

              <div style={styles.hrTight} />

              {/* ✅ ÚNICO indicador no card */}
              <div style={styles.totalSentBox}>
                <div style={styles.totalSentLabel}>Esta API já enviou</div>
                <div style={styles.totalSentValue}>{totalSent} mensagens</div>
              </div>

              {/* ✅ NOVO: fila + janela + safeguard (mínima alteração visual) */}
              <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={styles.vpsPillMini}>
                  <div style={styles.vpsPillLabel}>Fila</div>
                  <div style={styles.vpsPillValue}>
                    <b>{queue}</b> jobs
                  </div>
                </div>

                <div style={styles.vpsPillMini}>
                  <div style={styles.vpsPillLabel}>Envio</div>
                  <div style={styles.vpsPillValue}>{windowLine}</div>
                </div>

                <div style={styles.vpsPillMini}>
                  <div style={styles.vpsPillLabel}>Safeguard</div>
                  <div style={styles.vpsPillValue}>
                    {pausedLine}
                    {a.pauseReason ? <span style={{ opacity: 0.75 }}> • {a.pauseReason}</span> : null}
                  </div>
                </div>
              </div>

              <div style={styles.actionsRow}>
                <a style={styles.btn} target="_blank" href={`http://${location.hostname}:${a.port}/`}>
                  Abrir QR
                </a>

                <button style={styles.btnGhost} onClick={() => openMetrics(a)}>
                  Abrir Métricas
                </button>

                {paused ? (
                  <button
                    style={styles.btnWarn}
                    disabled={busy === a.folder}
                    onClick={() => actionUnpause(a.folder)}
                  >
                    {busy === a.folder ? "..." : "Tirar pausa"}
                  </button>
                ) : null}

                <button
                  style={styles.btnDanger}
                  disabled={busy === a.folder}
                  onClick={() => actionRestartLogout(a.folder)}
                >
                  {busy === a.folder ? "..." : "Reiniciar / Logout"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {total === 0 && !errApis && (
        <div style={{ ...styles.vpsCompact, marginTop: 14 }}>
          Nenhuma API encontrada em <b>/home/deploy/api</b> (precisa ter <b>config.json</b> em cada pasta).
        </div>
      )}

      {/* MODAL METRICAS (detalhes) */}
      {metricsOpen && metricsApi && (
        <div style={styles.modalOverlay} onClick={() => setMetricsOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900 }}>{metricsApi.folder} • Métricas</div>
                <div style={styles.small}>Detalhes de envio e status</div>
              </div>
              <button style={styles.btnGhost} onClick={() => setMetricsOpen(false)}>
                Fechar
              </button>
            </div>

            <div style={styles.hr} />

            <div style={styles.modalGrid}>
              <div style={styles.modalBox}>
                <div style={styles.metricLabel}>Status</div>
                <div style={styles.metricValue}>
                  {metricsApi.whatsStatus === "qr"
                    ? "QR"
                    : metricsApi.connected
                    ? "Online"
                    : "Offline"}
                </div>
              </div>

              <div style={styles.modalBox}>
                <div style={styles.metricLabel}>Número Conectado</div>
                <div style={{ ...styles.metricValue, fontSize: 14 }}>{metricsApi.connectedNumber || "-"}</div>
              </div>

              <div style={styles.modalBox}>
                <div style={styles.metricLabel}>Enviadas Hoje</div>
                <div style={styles.metricValue}>{metricsApi.metrics?.sentToday ?? 0}</div>
              </div>

              <div style={styles.modalBox}>
                <div style={styles.metricLabel}>Enviadas Total</div>
                <div style={styles.metricValue}>{metricsApi.metrics?.sentTotal ?? 0}</div>
              </div>

              <div style={styles.modalBox}>
                <div style={styles.metricLabel}>Data (contador diário)</div>
                <div style={{ ...styles.metricValue, fontSize: 14 }}>{metricsApi.metrics?.day ?? "-"}</div>
              </div>

              <div style={styles.modalBox}>
                <div style={styles.metricLabel}>Porta</div>
                <div style={styles.metricValue}>{metricsApi.port}</div>
              </div>

              {/* ✅ NOVO: fila */}
              <div style={styles.modalBox}>
                <div style={styles.metricLabel}>Fila (jobs)</div>
                <div style={styles.metricValue}>{metricsApi.queueSize ?? 0}</div>
              </div>

              {/* ✅ NOVO: janela */}
              <div style={styles.modalBox}>
                <div style={styles.metricLabel}>Janela de Envio</div>
                <div style={styles.metricValue}>{metricsApi.withinWindow ? "OK (08–19)" : "Fora do horário"}</div>
              </div>

              {/* ✅ NOVO: safeguard */}
              <div style={styles.modalBox}>
                <div style={styles.metricLabel}>Safeguard</div>
                <div style={{ ...styles.metricValue, fontSize: 14 }}>
                  {metricsApi.paused ? "Pausado" : "Ativo"}
                  {metricsApi.pausedUntil ? ` • até ${fmtTimeMaybe(metricsApi.pausedUntil)}` : ""}
                  {metricsApi.pauseReason ? ` • ${metricsApi.pauseReason}` : ""}
                </div>
              </div>
            </div>

            <div style={styles.hr} />

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <a style={styles.btn} target="_blank" href={`http://${location.hostname}:${metricsApi.port}/`}>
                Abrir QR
              </a>

              {metricsApi.paused ? (
                <button
                  style={styles.btnWarn}
                  disabled={busy === metricsApi.folder}
                  onClick={() => actionUnpause(metricsApi.folder)}
                >
                  {busy === metricsApi.folder ? "Processando..." : "Tirar pausa"}
                </button>
              ) : null}

              <button
                style={styles.btnDanger}
                disabled={busy === metricsApi.folder}
                onClick={() => actionRestartLogout(metricsApi.folder)}
              >
                {busy === metricsApi.folder ? "Processando..." : "Reiniciar / Logout"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, any> = {
  page: {
    minHeight: "100vh",
    background: "#0b1220",
    color: "#e7eefc",
    padding: 24,
    fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial",
  },
  topbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  controls: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  toggle: {
    display: "inline-flex",
    gap: 8,
    alignItems: "center",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    padding: "8px 10px",
    borderRadius: 12,
    fontSize: 12,
    opacity: 0.95,
  },
  h1: { fontSize: 22, fontWeight: 900, marginBottom: 4 },
  p: { fontSize: 13, opacity: 0.85 },
  small: { fontSize: 12, opacity: 0.85, marginTop: 4 },
  input: {
    background: "#0c1730",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 12,
    padding: "10px 12px",
    color: "#e7eefc",
    outline: "none",
  },
  btn: {
    background: "#2b66ff",
    border: "none",
    color: "white",
    padding: "10px 12px",
    borderRadius: 12,
    cursor: "pointer",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    minWidth: 110,
  },
  btnGhost: {
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "#e7eefc",
    padding: "10px 12px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 800,
    minWidth: 130,
  },
  btnDanger: {
    background: "#ff3b5c",
    border: "none",
    color: "white",
    padding: "10px 12px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 900,
    minWidth: 140,
  },
  btnWarn: {
    background: "rgba(234, 179, 8, 0.92)",
    border: "none",
    color: "#0b1220",
    padding: "10px 12px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 900,
    minWidth: 120,
  },

  // VPS compacto
  vpsCompact: {
    background: "#101b33",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 14,
    boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
    marginBottom: 14,
  },
  healthBadge: {
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#e7eefc",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  vpsPill: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 14,
    padding: "10px 12px",
    minWidth: 210,
  },
  vpsPillMini: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 14,
    padding: "10px 12px",
    minWidth: 170,
    flex: "1 1 170px",
  },
  vpsPillLabel: { fontSize: 11, opacity: 0.75, fontWeight: 800 },
  vpsPillValue: { fontSize: 13, fontWeight: 900, marginTop: 4 },

  // ✅ 3 colunas fixas (desktop)
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 14,
    marginTop: 10,
  },

  // card api compacto
  apiCardCompact: {
    background: "#101b33",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 14,
    boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
    minHeight: 170,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  apiHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
  },
  apiName: { fontSize: 18, fontWeight: 900 },
  apiSub: { fontSize: 12, opacity: 0.85, marginTop: 4 },

  hr: { height: 1, background: "rgba(255,255,255,0.08)", margin: "14px 0" },
  hrTight: { height: 1, background: "rgba(255,255,255,0.08)", margin: "12px 0" },

  badgeOk: {
    background: "rgba(24, 201, 100, 0.18)",
    border: "1px solid rgba(24, 201, 100, 0.45)",
    color: "#77f5b1",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  badgeOff: {
    background: "rgba(255, 59, 92, 0.18)",
    border: "1px solid rgba(255, 59, 92, 0.45)",
    color: "#ffb3c0",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  badgeQr: {
    background: "rgba(234, 179, 8, 0.18)",
    border: "1px solid rgba(234, 179, 8, 0.45)",
    color: "#ffe08a",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  badgeMeta: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#e7eefc",
    padding: "5px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    opacity: 0.95,
    whiteSpace: "nowrap",
  },

  totalSentBox: {
    background: "#0c1730",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    padding: 12,
  },
  totalSentLabel: { fontSize: 12, opacity: 0.78, fontWeight: 800 },
  totalSentValue: { fontSize: 18, fontWeight: 900, marginTop: 6 },

  actionsRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 12,
  },

  // erros
  errorCard: {
    background: "rgba(255, 59, 92, 0.12)",
    border: "1px solid rgba(255, 59, 92, 0.35)",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
    marginBottom: 14,
  },

  // modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 9999,
  },
  modal: {
    width: "min(860px, 100%)",
    background: "#101b33",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 18,
    padding: 16,
    boxShadow: "0 24px 90px rgba(0,0,0,0.55)",
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 10,
  },
  modalBox: {
    background: "#0c1730",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 14,
    padding: 12,
  },
  metricLabel: { fontSize: 12, opacity: 0.8 },
  metricValue: { fontSize: 16, fontWeight: 900, marginTop: 6 },
};
